import { handleError, handleFormValidationErrors } from "@/utils/error";
import { APIError } from "web-core/utils";
import { MobileViewContainer } from "@core/components/layout/mobile-view-container";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute } from "@tanstack/react-router";
import { imageAssetUrl } from "@/utils/url";
import {
  getDistributorByHandleOptions,
  useCreateClient,
  useGenerateOtp,
  useLoginAsInvestor,
  useLogout,
  useSignupAsInvestor,
} from "creso-core/hooks";
import { getInitials, getPromiseWithResolvers } from "creso-core/utils";
import { nameSchema, panSchema, phoneNumberSchema } from "creso-core/zod";
import { CheckIcon, Loader2, PhoneCallIcon } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useMediaQuery } from "usehooks-ts";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  cn,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Drawer,
  DrawerContent,
  DrawerTitle,
  Form,
  FormField,
  FormInput,
  FormPrefixedInput,
  InputOTP,
  InputOTPSlot,
  ThrottledButton,
  V2Dialog,
} from "web-design-system";
import { z, object } from "zod";

export const Route = createFileRoute("/(auth)/(headless)/mfd/$handle")({
  component: RouteComponent,
  async beforeLoad({ params, context, location }) {
    const { queryClient } = context;
    const { handle } = params;
    const distributorInfo = await queryClient.ensureQueryData(
      getDistributorByHandleOptions({ handle })
    );
    const subdomain = distributorInfo.subdomain_name || handle;
    const webAppUrl = `https://${subdomain}.creso.in`;
    const onboardingLink = `${webAppUrl}/onboard${location.searchStr}`;
    window.location.replace(onboardingLink);
  },
  loader: async ({ params, context }) => {
    const { queryClient } = context;

    const { handle } = params;

    const response = await queryClient.fetchQuery(
      getDistributorByHandleOptions({ handle })
    );

    return response;
  },
});

const ReferralSchema = object({
  phone_number: phoneNumberSchema,
  name: nameSchema,
  pan: panSchema,
});

function RouteComponent() {
  const info = Route.useLoaderData();

  const employeeIdFromUrl =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("employee_id")
      : null;

  const { mutateAsync: generateOtp } = useGenerateOtp();
  const { mutateAsync: login } = useLoginAsInvestor();
  const { mutateAsync: signup } = useSignupAsInvestor();

  const { mutateAsync: logout } = useLogout();

  const { mutateAsync: createClient } = useCreateClient();

  const otpPromise = useRef(getPromiseWithResolvers<string>());

  const [formState, setFormState] = useState<
    "not-started" | "otp-generated" | "verified" | "errored"
  >("not-started");

  const form = useForm({
    resolver: zodResolver(ReferralSchema),
    defaultValues: {
      phone_number: "",
      name: "",
      pan: "",
    },
  });

  const handleSubmit = async (data: z.infer<typeof ReferralSchema>) => {
    try {
      otpPromise.current = getPromiseWithResolvers<string>();

      await generateOtp({ phone_number: data.phone_number });
      setFormState("otp-generated");

      const otp = await otpPromise.current?.promise;

      try {
        await login({
          phone_number: data.phone_number,
          otp,
          code: "",
        });
      } catch {
        try {
          await signup({
            phone_number: data.phone_number,
            phone_otp: otp,
            email: "",
            email_otp: "",
          });
        } catch (error) {
          if (error instanceof APIError) {
            if (error.data.code === "PHONE_NUMBER_ALREADY_EXISTS") {
              form.setError("phone_number", {
                message: "Phone number already exists",
              });
            }
            setFormState("errored");
            return;
          }
          throw error;
        }
      }

      await createClient({
        name: data.name,
        pan: data.pan,
        phone_number: data.phone_number,
        // @ts-expect-error strongly type createClient later
        arn_code: info.arn_code,
        ...(employeeIdFromUrl ? { employee_id: employeeIdFromUrl } : {}),
      });

      setFormState("verified");
    } catch (error) {
      handleFormValidationErrors(form, error);
      handleError(error, "Failed to create account");
      setFormState("errored");
    } finally {
      await logout();
    }
  };

  const submitOtp = useCallback(
    (otp: string) => {
      otpPromise.current?.resolve(otp);
    },
    [otpPromise]
  );

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  const handleDownloadApp = () => {
    if (isIOS) {
      window.open(
        "https://apps.apple.com/in/app/creso-sips-and-mutual-funds/id6745787516"
      );
    } else {
      window.open(
        "https://play.google.com/store/apps/details?id=creso.mobile&hl=en_IN"
      );
    }
  };

  return (
    <MobileViewContainer>
      <div className="p-6 flex flex-1 gap-6 flex-col">
        <div className="flex gap-3 items-center pb-6 border-b border-border">
          <Avatar className="size-12 rounded-md">
            <AvatarImage
              className="size-full object-contain"
              src={info.brand_logo}
            />
            <AvatarFallback className="bg-primary rounded-md size-full text-primary-foreground font-semibold">
              {getInitials(info.brand_name || info.name)}
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col gap-1">
            <span className="text-lg font-semibold">
              {info.brand_name || info.name}
            </span>
            <span className="text-sm text-muted-foreground">
              {info.tag_line || "AMFI registered Mutual Fund Distributor"}
            </span>
          </div>
          <Button
            className="ml-auto"
            variant="outline"
            size="icon"
            onClick={() => {
              window.location.href = `tel:${info.phone_number}`;
            }}
          >
            <PhoneCallIcon />
          </Button>
        </div>
        {formState !== "verified" ? (
          <div className="flex flex-col flex-1 pt-6">
            <div className={cn("flex flex-col gap-6 flex-1")}>
              <span className="text-lg font-semibold">
                Start your investment journey
              </span>
              <span className="text-sm">
                Hi, your wealth manager{" "}
                <span className="font-semibold">
                  {info.name} ({info.arn_code})
                </span>{" "}
                wants to setup your account on Creso.
              </span>
              <div className="flex flex-col flex-1 gap-6 py-3">
                <Form {...form}>
                  <FormField
                    name="name"
                    render={({ field }) => (
                      <FormInput label="Name" {...field} />
                    )}
                  />
                  <FormField
                    name="phone_number"
                    render={({ field }) => (
                      <FormPrefixedInput
                        inputMode="numeric"
                        prefix="+91"
                        label="Mobile"
                        {...field}
                      >
                        +91
                      </FormPrefixedInput>
                    )}
                  />
                  <FormField
                    name="pan"
                    render={({ field }) => (
                      <FormInput
                        label="PAN"
                        {...field}
                        onChange={(val) =>
                          field.onChange(val?.toLocaleUpperCase() || "")
                        }
                      />
                    )}
                  />
                  <div className="mt-auto">
                    <span className={cn("text-sm")}>
                      By continuing, you agree to our{" "}
                      <a
                        className="underline"
                        href="https://creso.in/terms-and-conditions"
                        target="_blank"
                        rel="noreferrer"
                      >
                        Terms & Conditions
                      </a>
                      .
                    </span>
                    <Button
                      type="submit"
                      onClick={form.handleSubmit(handleSubmit)}
                      size="lg"
                      className={cn("w-full mt-2")}
                    >
                      Submit
                    </Button>
                  </div>
                </Form>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-6 items-center flex-1 justify-center">
            <div className="text-sm w-full p-3 flex items-center rounded-md border border-constructive font-semibold bg-constructive-foreground text-constructive">
              <CheckIcon className="size-4 mr-2" /> Account setup initiated!
            </div>
            <div className="flex flex-col gap-4 w-full">
              <span className="text-sm font-semibold">Next steps</span>
              <div className="flex items-center gap-2">
                <div className="size-4 text-xs text-primary-foreground text-center rounded-full bg-slate-800">
                  1
                </div>
                <span className="text-sm">Download and login to Creso app</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="size-4 text-xs text-primary-foreground text-center rounded-full bg-slate-800">
                  2
                </div>
                <span className="text-sm">Fill and verify your details</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="size-4 text-xs text-primary-foreground text-center rounded-full bg-slate-800">
                  3
                </div>
                <span className="text-sm">
                  Authenticate with OTP, and complete account creation
                </span>
              </div>
            </div>
            <div className="px-10 w-full flex-1 gap-4 pt-2 justify-between items-center flex relative overflow-hidden bg-slate-200 rounded-3xl flex-col">
              <div
                style={{
                  backgroundImage: `url('${imageAssetUrl("download-app.webp")}')`,
                }}
                className="bg-cover flex-1 w-full"
              />
              <div
                style={{
                  background: "rgba(241, 245, 249, 0.69)",
                  backdropFilter: "blur(6px)",
                }}
                className="flex flex-row p-4 items-center w-full rounded-b-3xl absolute bottom-0 left-0 gap-2"
              >
                <div className="flex flex-row gap-2 items-center">
                  <img
                    className="size-9"
                    src={imageAssetUrl("creso-app.jpg")}
                    height={36}
                    width={36}
                  />
                  <span className="text-sm font-semibold">
                    Creso: SIPs and Mutual Funds
                  </span>
                </div>
                <div className="flex flex-shrink-0 flex-row gap-2 justify-end ml-auto">
                  <a href="https://play.google.com/store/apps/details?id=creso.mobile&hl=en_IN">
                    <img
                      className="size-9"
                      src={imageAssetUrl("play-store.webp")}
                      height={36}
                      width={36}
                    />
                  </a>
                  <a href="https://apps.apple.com/in/app/creso-sips-and-mutual-funds/id6745787516">
                    <img
                      className="size-9"
                      src={imageAssetUrl("app-store.webp")}
                      height={36}
                      width={36}
                    />
                  </a>
                </div>
              </div>
            </div>
            <Button
              onClick={handleDownloadApp}
              size="lg"
              className="w-full flex-shrink-0 mt-auto"
            >
              Download App
            </Button>
          </div>
        )}
        {formState === "otp-generated" && (
          <OtpWindow
            regen={() => {
              generateOtp({
                phone_number: form.watch("phone_number"),
              });
            }}
            onSubmit={submitOtp}
          />
        )}
      </div>
    </MobileViewContainer>
  );
}

function OtpWindow(props: {
  regen: () => void;
  onSubmit: (otp: string) => void;
}) {
  const isDesktop = useMediaQuery("(min-width: 768px)");

  const [otp, setOtp] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = () => {
    setIsSubmitting(true);
    props.onSubmit(otp);
  };

  if (isDesktop) {
    return (
      <V2Dialog open>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              <h5>Enter OTP to create account</h5>
            </DialogTitle>
          </DialogHeader>
          <InputOTP
            onChange={(value) => setOtp(value)}
            className="w-full mt-2"
            maxLength={6}
          >
            <InputOTPSlot index={0} />
            <InputOTPSlot index={1} />
            <InputOTPSlot index={2} />
            <InputOTPSlot index={3} />
            <InputOTPSlot index={4} />
            <InputOTPSlot index={5} />
          </InputOTP>
          <div className="flex flex-row justify-end gap-2">
            <ThrottledButton
              throttle={25}
              className="mt-2 flex-shrink-0"
              size="lg"
              variant="outline"
              type="button"
              beginOnMount
              onClick={props.regen}
            >
              Resend OTP
            </ThrottledButton>
            <Button
              className="mt-2 w-full"
              size="lg"
              type="submit"
              disabled={isSubmitting}
              onClick={handleSubmit}
            >
              {isSubmitting ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                "Submit OTP"
              )}
            </Button>
          </div>
        </DialogContent>
      </V2Dialog>
    );
  }

  return (
    <Drawer open>
      <DrawerContent className="px-4 pb-4 !rounded-t-none">
        <DrawerTitle className="text-left">
          <h5>Enter OTP to create account</h5>
        </DrawerTitle>
        <div className="flex flex-col mt-2 items-start gap-4">
          <InputOTP
            onChange={(value) => setOtp(value)}
            className="w-full"
            maxLength={6}
          >
            <InputOTPSlot index={0} />
            <InputOTPSlot index={1} />
            <InputOTPSlot index={2} />
            <InputOTPSlot index={3} />
            <InputOTPSlot index={4} />
            <InputOTPSlot index={5} />
          </InputOTP>
          <Button
            className="w-full sm:w-full md:w-fit lg:w-fit"
            onClick={handleSubmit}
            size="lg"
          >
            {isSubmitting ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              "Submit OTP"
            )}
          </Button>
          <ThrottledButton
            throttle={25}
            type="button"
            className="w-full sm:w-full md:w-fit lg:w-fit px-2"
            onClick={props.regen}
            size="lg"
            variant="outline"
          >
            Resend OTP
          </ThrottledButton>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
